import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Box, IconButton } from '@mui/material';
import { Store, Restore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { shopService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import { DeleteConfirmationDialog, LoadingSpinner } from '@/components';
import WcnTable, { DataTableColumn } from '@/components/datatable/WcnTable';
import { Shop } from '@core/types';
import DeleteShopConfirmationDialog, {
    DeleteShopConfirmationDialogRef
} from '@/components/dialog/DeleteShopConfirmationDialog.tsx';
import { RecycleBinChildRef } from '../../RecycleBin';
import { useApiStore } from '@core/store';

const ShopsRecycleBin = forwardRef<RecycleBinChildRef>((props, ref) => {
    const { t } = useTranslation();
    const { notifySuccess } = useNotifications();

    const [loading, setLoading] = useState(false);
    const [shops, setShops] = useState<Shop[]>([]);
    const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
    const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
    const { loadShopIds } = useApiStore();

    const deleteConfirmationDialogRef = useRef<DeleteShopConfirmationDialogRef>(null);

    const loadDeletedShops = useCallback(async () => {
        setLoading(true);
        try {
            const deletedShops = await shopService.loadRecycledShops();
            setShops(deletedShops);
        } catch (error) {
            console.error('Error loading deleted shops:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Expose reload method to parent
    useImperativeHandle(ref, () => ({
        reload: () => {
            loadDeletedShops();
        }
    }));

    useEffect(() => {
        loadDeletedShops();
    }, [loadDeletedShops]);

    const askDeleteShop = useCallback((shop: Shop) => {
        setSelectedShop(shop);
        deleteConfirmationDialogRef.current?.openDialog();
    }, []);

    const handleDeleteShop = useCallback(async () => {
        if (!selectedShop) return;

        deleteConfirmationDialogRef.current?.close();
        setLoading(true);

        try {
            await shopService.deleteShopPermanently(selectedShop.id);
            notifySuccess();
            await loadDeletedShops();
        } catch (error) {
            console.error('Error deleting shop:', error);
        } finally {
            setLoading(false);
            setSelectedShop(null);
        }
    }, [selectedShop, loadDeletedShops]);

    const askRestoreShop = useCallback((shop: Shop) => {
        setSelectedShop(shop);
        setRestoreDialogOpen(true);
    }, []);

    const handleRestoreShop = useCallback(async () => {
        if (!selectedShop) return;

        setRestoreDialogOpen(false);
        setLoading(true);

        try {
            await shopService.restoreShopFromRecycleBin(selectedShop.id);
            notifySuccess();
            await Promise.all([loadDeletedShops(), loadShopIds()]);
        } catch (error) {
            console.error('Error restoring shop:', error);
        } finally {
            setLoading(false);
            setSelectedShop(null);
        }
    }, [selectedShop, loadDeletedShops]);

    const columns: DataTableColumn<Shop>[] = [
        {
            text: '',
            value: 'icon',
            width: 60,
            sortable: false,
            render: () => <Store />
        },
        {
            text: t('name'),
            value: 'name'
        },
        {
            text: t('deleteDate'),
            value: 'deleteDate',
            render: (row) => (row.deleteDate ? new Date(row.deleteDate).toLocaleDateString() : '')
        },
        {
            text: t('finalDeleteDate'),
            value: 'permanentDeleteDate',
            render: (row) => (row.permanentDeleteDate ? new Date(row.permanentDeleteDate).toLocaleDateString() : '')
        },
        {
            text: '',
            value: 'actions',
            width: 100,
            align: 'right',
            sortable: false,
            render: (row) => (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" onClick={() => askRestoreShop(row)} title={t('restore')}>
                        <Restore fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => askDeleteShop(row)} title={t('delete')}>
                        <i className="bi bi-trash3" />
                    </IconButton>
                </Box>
            )
        }
    ];

    return (
        <Box>
            {loading && <LoadingSpinner showBackground={false} />}

            <Box sx={{ py: 3 }}>
                {!loading && shops.length >= 0 && (
                    <WcnTable
                        columns={columns}
                        data={shops}
                        loading={loading}
                        noDataMessage={t('noData')}
                        size="small"
                    />
                )}
            </Box>

            {/* Restore Dialog */}
            <DeleteConfirmationDialog
                open={restoreDialogOpen}
                heading={t('restoreHeading')}
                message={t('restoreShopMessage', { item: selectedShop?.name || '' })}
                onConfirm={handleRestoreShop}
                onCancel={() => {
                    setRestoreDialogOpen(false);
                    setSelectedShop(null);
                }}
            />

            {/* Delete Dialog with shop name confirmation */}
            <DeleteShopConfirmationDialog
                dialogRef={deleteConfirmationDialogRef}
                heading={t('deleteHeading')}
                message={t('deleteShopMessage', { item: selectedShop?.name || '' })}
                expectedShopName={selectedShop?.name || ''}
                onConfirm={handleDeleteShop}
                onClose={() => setSelectedShop(null)}
            />
        </Box>
    );
});

ShopsRecycleBin.displayName = 'ShopsRecycleBin';
export default ShopsRecycleBin;
